import React, { Children, cloneElement, Component, Fragment } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Trans, withTranslation } from 'react-i18next';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import sortBy from 'lodash/sortBy';
import Cookies from 'js-cookie';

import { Button } from 'formlabs-web-components/Link';
import { Header } from 'formlabs-web-components/Type';

import { formatMagentoError } from './utils';
import { continueShoppingRedirects } from './continueShopping';
import CheckoutButton from './CheckoutButton';
import ProductImage from './ProductImage';
import UpsellProducts from './UpsellProducts';
import CenterModal from '../modal/CenterModal';

import { addToCart as addToCartAction } from '../../ducks/cart';
import useFetchProducts from '../../hooks/useFetchProducts';

import './AddToCart.module.css';

const Upsell = ({ sku, storeId, t }) => {
  const { loading, data } = useFetchProducts(storeId, sku);

  if (loading || !data || !data.length) {
    return null;
  }

  const productLinks = data[0].product_links;
  const upsellProducts = productLinks
    ? sortBy(
        productLinks
          .filter(link => link.link_type === 'upsell')
          .map(link => link.linked_product_sku),
        ['position']
      )
    : null;

  if (!upsellProducts || !upsellProducts.length) {
    return null;
  }

  return (
    <div styleName="upsell">
      <div styleName="upsell-header">
        <Header level={2} displayAs={4} weight={400}>
          {t("Don't forget")}
        </Header>
      </div>
      <UpsellProducts skus={upsellProducts} />
    </div>
  );
};

class AddToCart extends Component {
  state = {
    error: null,
    showModal: false
  };

  closeModal = event => {
    if (this.state.unlisten) {
      this.state.unlisten();
    }
    this.setState({ showModal: false, error: null });

    const { pathname } = this.props.location;
    const history = this.props.history;
    if (pathname in continueShoppingRedirects) {
      history.push(continueShoppingRedirects[pathname]);
      if (pathname === continueShoppingRedirects[pathname]) {
        history.go(0);
      }
    }
  };

  openModal = error => {
    this.setState({
      unlisten: this.props.history.listen(() => {
        this.closeModal();
      }),
      showModal: true,
      error
    });
  };

  handleClick = async event => {
    const {
      addToCart,
      amount,
      sku,
      bundleOptions,
      theBetterBundleOptions,
      quickAdd,
      onSuccess,
      disableModal = false
    } = this.props;

    const item = {
      sku,
      qty: amount
    };

    const context = quickAdd ? 'quick-add' : 'normal';

    addToCart(
      item,
      bundleOptions,
      theBetterBundleOptions,
      context,
      (error = null) => {
        let errorMessage = null;
        if (error && window.newrelic) {
          errorMessage = formatMagentoError(error.response);
          const errorAttr = {
            method: 'addToCart',
            cart: Cookies.get('cart') || 'null',
            errorMessage
          };
          console.error(errorMessage, error.response, errorAttr);
          window.newrelic.noticeError(error, errorAttr);
        }
        if (!disableModal) {
          this.openModal(errorMessage);
        }
        if (onSuccess) {
          onSuccess();
        }
      }
    );
  };

  render() {
    const {
      name,
      t,
      title,
      quickAdd,
      sku,
      loading,
      storeId,
      checkoutButtonText,
      checkoutModalComment,
      children
    } = this.props;
    const { showModal, error } = this.state;

    const buttonTitle = title || t('Buy now');

    const button = children ? (
      cloneElement(Children.only(children), {
        onClick: this.handleClick,
        loading
      })
    ) : (
      <Button
        type="button"
        onClick={this.handleClick}
        small={quickAdd}
        loading={loading}
      >
        {quickAdd ? (
          <span styleName="quick">
            <span>{t('Add')}</span>
            <span>+</span>
          </span>
        ) : (
          buttonTitle
        )}
      </Button>
    );

    return (
      <Fragment>
        {button}

        {showModal && (
          <CenterModal
            id="addToCart"
            onClose={() => {
              this.closeModal();
            }}
          >
            {error ? (
              <p styleName="modal-title">
                <strong>{t('Error')}:</strong> {error}
              </p>
            ) : (
              <>
                <p styleName="modal-title">
                  <ProductImage sku={sku} alt={name} />
                  <span>
                    <Trans>
                      <strong>{{ name }}</strong> has been added to your cart.
                    </Trans>
                  </span>
                </p>
                {checkoutModalComment && <p>{checkoutModalComment}</p>}
              </>
            )}

            <div styleName="actions">
              <CheckoutButton
                withArrow
                checkoutButtonText={checkoutButtonText}
              />
              <Button type="button" onClick={this.closeModal} color="light">
                {t('Continue shopping')}
              </Button>
            </div>

            <Upsell sku={sku} storeId={storeId} t={t} />
          </CenterModal>
        )}
      </Fragment>
    );
  }
}

AddToCart.propTypes = {
  amount: PropTypes.number.isRequired,
  sku: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  quickAdd: PropTypes.bool
};

AddToCart.defaultProps = {
  bundleOptions: null,
  title: null,
  quickAdd: false
};

export default compose(
  connect(
    (state, ownProps) => {
      return {
        storeId: state.store.id,
        loading: state.cart.loading === ownProps.sku
      };
    },
    dispatch => {
      return {
        addToCart: (
          item,
          bundleOptions,
          theBetterBundleOptions,
          context,
          callback
        ) => {
          dispatch(
            addToCartAction(
              item,
              bundleOptions,
              theBetterBundleOptions,
              context,
              callback
            )
          );
        }
      };
    }
  ),
  withTranslation(),
  withRouter
)(AddToCart);
